/**
 * What the pointer is on: a small card beside a hovered concept with its
 * name, its memory state and the lesson it came from. Desktop only; a phone
 * has no hover, and a tap opens the peek card instead.
 *
 * It used to be a bare name (and only once zoomed in far enough for names at
 * all), so learning a dot's state meant opening the Legend or the panel. Now
 * the colour explains itself where you are looking: the same swatch, the same
 * word the Legend and the panel use (`legendLabel`), and the recall figure,
 * marked "est." when it is an estimate (`isEstimated`).
 *
 * Positioned in screen pixels from the node's canvas position, beside the
 * dot rather than over it, and flipped to the left near the right edge so it
 * never runs off the canvas. `pointer-events-none`: it must never steal the
 * hover from the dot it describes.
 */

import { useIsDarkMode } from '@/hooks/ui/themeClass';
import {
  PAUSED_ALPHA,
  canvasPalette,
} from '@/components/features/mindspace/graph/graphPalette';
import { legendLabel, type MemoryTier } from '@/lib/mindspace/memoryTier';

/** Card width in px, for the edge flip. Matches the `w-60` below. */
const CARD_WIDTH = 240;
/** Gap between the dot and the card, in px. */
const OFFSET = 14;

export interface HoverCardConcept {
  name: string;
  tier?: MemoryTier;
  recall?: number | null;
  recallEstimated?: boolean;
  isPaused?: boolean;
}

export function GraphHoverCard({
  concept,
  lesson,
  x,
  y,
  containerWidth,
}: {
  concept: HoverCardConcept;
  /** The lesson title, when it has loaded. */
  lesson?: string;
  /** The dot's centre, in px within the graph container. */
  x: number;
  y: number;
  containerWidth: number;
}) {
  const isDark = useIsDarkMode();
  const { nodeTier, nodeInk } = canvasPalette(isDark);
  const tier = concept.tier ?? 'strong';
  const flip = x + OFFSET + CARD_WIDTH > containerWidth - 8;
  const recall =
    tier === 'new' || concept.recall == null
      ? null
      : `${concept.recallEstimated ? '~' : ''}${Math.round(concept.recall * 100)}%`;

  return (
    <div
      role='tooltip'
      className='pointer-events-none absolute z-10 w-60 -translate-y-1/2 rounded-lg border border-border bg-card/95 px-3 py-2.5 shadow-elevation-2 backdrop-blur-sm motion-safe:duration-150 motion-safe:animate-in motion-safe:fade-in-0'
      style={{ left: flip ? x - OFFSET - CARD_WIDTH : x + OFFSET, top: y }}
    >
      <p className='line-clamp-2 text-sm font-semibold leading-snug text-foreground'>
        {concept.name}
      </p>
      <p className='mt-1.5 flex items-center gap-2 text-xs text-muted-foreground'>
        {concept.isPaused ? (
          <span
            aria-hidden='true'
            className='h-2.5 w-2.5 flex-shrink-0 rounded-full border-2'
            style={{ borderColor: nodeInk(PAUSED_ALPHA) }}
          />
        ) : (
          <span
            aria-hidden='true'
            className='h-2.5 w-2.5 flex-shrink-0 rounded-full'
            style={{ backgroundColor: nodeTier(tier) }}
          />
        )}
        <span className='font-medium text-foreground'>
          {concept.isPaused ? 'Paused' : legendLabel(tier)}
        </span>
        {recall && !concept.isPaused && (
          <span className='ml-auto font-mono tabular-nums'>
            {recall}
            {concept.recallEstimated && ' est.'}
          </span>
        )}
      </p>
      {lesson && (
        <p className='mt-1 truncate text-xs text-muted-foreground'>
          From {lesson}
        </p>
      )}
    </div>
  );
}
